import React, { useState, useEffect } from 'react';
import { Save, Loader2, Plus, X, DollarSign, Layers, Wrench } from 'lucide-react';
import { signageAPI } from '../../services/api';

const RateCardEditor = () => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('base');

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await signageAPI.getRateCard();
        setCard(data);
      } catch (err) { console.error('Error:', err); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await signageAPI.updateRateCard(card);
      setCard(updated);
      alert('Rate card saved successfully');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const updateMaterial = (section, idx, field, value) => {
    const items = [...(card[section] || [])];
    items[idx] = { ...items[idx], [field]: field === 'rate' ? Number(value) : value };
    setCard({ ...card, [section]: items });
  };

  const addMaterial = (section, category) => {
    const items = [...(card[section] || [])];
    items.push({ name: '', rate: 0, category });
    setCard({ ...card, [section]: items });
  };

  const removeMaterial = (section, idx) => {
    const items = (card[section] || []).filter((_, i) => i !== idx);
    setCard({ ...card, [section]: items });
  };

  const updateFabItem = (section, idx, field, value) => {
    const items = [...(card[section] || [])];
    items[idx] = { ...items[idx], [field]: field === 'weight_per_20ft' ? Number(value) : value };
    setCard({ ...card, [section]: items });
  };

  const addFabItem = (section) => {
    const items = [...(card[section] || [])];
    items.push({ name: '', weight_per_20ft: 0 });
    setCard({ ...card, [section]: items });
  };

  const removeFabItem = (section, idx) => {
    const items = (card[section] || []).filter((_, i) => i !== idx);
    setCard({ ...card, [section]: items });
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  if (!card) return <div className="text-center py-12 text-sm text-gray-500">Failed to load rate card</div>;

  const sections = [
    { id: 'base', label: 'Base Materials', icon: Layers },
    { id: 'retro', label: 'Retro Sheets', icon: Layers },
    { id: 'work', label: 'Work Types', icon: Wrench },
    { id: 'fabrication', label: 'Post & Frame', icon: Wrench },
    { id: 'rates', label: 'General Rates', icon: DollarSign },
  ];

  return (
    <div data-testid="rate-card-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Signage Rate Card</h2>
          <p className="text-[11px] text-gray-500">Manage material costs, labour rates, and fabrication parameters</p>
        </div>
        <button data-testid="save-rate-card" onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save Changes
        </button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl w-fit">
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-all ${activeSection === s.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <s.icon className="w-3.5 h-3.5" />{s.label}
          </button>
        ))}
      </div>

      {/* Base Materials */}
      {activeSection === 'base' && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-900">Base Materials (ACP/ALU)</h3>
            <button onClick={() => addMaterial('base_materials', 'base')} className="text-[10px] font-semibold px-2 py-1 rounded-md hover:bg-blue-50" style={{ color: '#24549c' }}>+ Add</button>
          </div>
          <div className="space-y-2">
            {(card.base_materials || []).map((m, i) => (
              <div key={i} className="flex items-center gap-3">
                <input value={m.name} onChange={e => updateMaterial('base_materials', i, 'name', e.target.value)} placeholder="Material name" className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-400">INR</span>
                  <input type="number" value={m.rate} onChange={e => updateMaterial('base_materials', i, 'rate', e.target.value)} className="w-24 px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                  <span className="text-[10px] text-gray-400">/sqft</span>
                </div>
                <button onClick={() => removeMaterial('base_materials', i)} className="p-1 rounded hover:bg-red-50"><X className="w-3.5 h-3.5 text-red-400" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Retro Sheets */}
      {activeSection === 'retro' && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-900">Retro Reflective Sheets</h3>
            <button onClick={() => addMaterial('retro_sheets', 'retro')} className="text-[10px] font-semibold px-2 py-1 rounded-md hover:bg-blue-50" style={{ color: '#24549c' }}>+ Add</button>
          </div>
          <div className="space-y-2">
            {(card.retro_sheets || []).map((m, i) => (
              <div key={i} className="flex items-center gap-3">
                <input value={m.name} onChange={e => updateMaterial('retro_sheets', i, 'name', e.target.value)} placeholder="Sheet name" className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-400">INR</span>
                  <input type="number" value={m.rate} onChange={e => updateMaterial('retro_sheets', i, 'rate', e.target.value)} className="w-24 px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                  <span className="text-[10px] text-gray-400">/sqft</span>
                </div>
                <button onClick={() => removeMaterial('retro_sheets', i)} className="p-1 rounded hover:bg-red-50"><X className="w-3.5 h-3.5 text-red-400" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Work Types */}
      {activeSection === 'work' && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-900">Work Types</h3>
            <button onClick={() => addMaterial('work_types', 'work')} className="text-[10px] font-semibold px-2 py-1 rounded-md hover:bg-blue-50" style={{ color: '#24549c' }}>+ Add</button>
          </div>
          <div className="space-y-2">
            {(card.work_types || []).map((m, i) => (
              <div key={i} className="flex items-center gap-3">
                <input value={m.name} onChange={e => updateMaterial('work_types', i, 'name', e.target.value)} placeholder="Work type" className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-400">INR</span>
                  <input type="number" value={m.rate} onChange={e => updateMaterial('work_types', i, 'rate', e.target.value)} className="w-24 px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                  <span className="text-[10px] text-gray-400">/sqft</span>
                </div>
                <button onClick={() => removeMaterial('work_types', i)} className="p-1 rounded hover:bg-red-50"><X className="w-3.5 h-3.5 text-red-400" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Post & Frame */}
      {activeSection === 'fabrication' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-gray-900">Post Types</h3>
              <button onClick={() => addFabItem('post_types')} className="text-[10px] font-semibold px-2 py-1 rounded-md hover:bg-blue-50" style={{ color: '#24549c' }}>+ Add</button>
            </div>
            <div className="space-y-2">
              {(card.post_types || []).map((p, i) => (
                <div key={i} className="flex items-center gap-3">
                  <input value={p.name} onChange={e => updateFabItem('post_types', i, 'name', e.target.value)} placeholder="e.g. 76 OD" className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                  <div className="flex items-center gap-1">
                    <input type="number" value={p.weight_per_20ft} onChange={e => updateFabItem('post_types', i, 'weight_per_20ft', e.target.value)} className="w-24 px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">kg / 20ft</span>
                  </div>
                  <button onClick={() => removeFabItem('post_types', i)} className="p-1 rounded hover:bg-red-50"><X className="w-3.5 h-3.5 text-red-400" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-gray-900">Frame Types</h3>
              <button onClick={() => addFabItem('frame_types')} className="text-[10px] font-semibold px-2 py-1 rounded-md hover:bg-blue-50" style={{ color: '#24549c' }}>+ Add</button>
            </div>
            <div className="space-y-2">
              {(card.frame_types || []).map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <input value={f.name} onChange={e => updateFabItem('frame_types', i, 'name', e.target.value)} placeholder="e.g. 25x25x3" className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                  <div className="flex items-center gap-1">
                    <input type="number" value={f.weight_per_20ft} onChange={e => updateFabItem('frame_types', i, 'weight_per_20ft', e.target.value)} className="w-24 px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">kg / 20ft</span>
                  </div>
                  <button onClick={() => removeFabItem('frame_types', i)} className="p-1 rounded hover:bg-red-50"><X className="w-3.5 h-3.5 text-red-400" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* General Rates */}
      {activeSection === 'rates' && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-gray-900 mb-3">Default Rates</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-1">Labour Rate (per sqft)</label>
              <input type="number" value={card.labour_rate} onChange={e => setCard({ ...card, labour_rate: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-1">Margin (%)</label>
              <input type="number" value={card.margin_percent} onChange={e => setCard({ ...card, margin_percent: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-1">Post Per Kg Rate (INR)</label>
              <input type="number" value={card.post_per_kg_rate} onChange={e => setCard({ ...card, post_per_kg_rate: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-1">Frame Per Kg Rate (INR)</label>
              <input type="number" value={card.frame_per_kg_rate} onChange={e => setCard({ ...card, frame_per_kg_rate: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-1">Installation Per Pcs (INR)</label>
              <input type="number" value={card.installation_per_pcs} onChange={e => setCard({ ...card, installation_per_pcs: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateCardEditor;
